import { createInstance, Identify, Types } from '@amplitude/analytics-browser';
import shouldDisableTracking from '@calm/web-common/dist/shouldDisableTracking';
import { Environment } from '@rhg/constants';
import { getUserBrowser, getUserOs } from '@rhg/utils/browser';
import { GenericAnalyticsDestination } from '../types';

export const FRONTEND_INSTANCE_NAME = 'frontend';

const frontendInstance = createInstance();

export function enrichAmplitudeEvent(event: Types.Event): Promise<Types.Event> {
    const browser = getUserBrowser();
    const os = getUserOs();

    const existingSet: unknown = event.user_properties?.$set;
    event.user_properties = {
        ...event.user_properties,
        $set: {
            ...(existingSet && typeof existingSet === 'object'
                ? existingSet
                : {}),
            BrowserType: browser.name,
            BrowserVersion: browser.version,
        },
    };

    event.os_name = os.name;
    event.os_version = os.version;
    return Promise.resolve(event);
}

/**
 * Setup the amplitude plugin to track browser and os information
 */
function amplitudeSetup() {
    const plugin: Types.Plugin = {
        name: 'track-browser-os',
        type: 'enrichment',
        execute: enrichAmplitudeEvent,
    };
    frontendInstance.add(plugin);
}

type AmplitudeDestination = GenericAnalyticsDestination<
    'amplitude',
    Types.BrowserClient
>;

export function createAmplitudeDestination(
    token: string,
    env: Environment,
    baseUrl: string
): AmplitudeDestination {
    amplitudeSetup();
    return {
        name: 'amplitude',
        init: () =>
            frontendInstance.init(token, {
                instanceName: FRONTEND_INSTANCE_NAME,
                serverUrl: baseUrl,
                logLevel: Types.LogLevel.None,
                optOut: shouldDisableTracking(env === Environment.LOCAL),
                trackingOptions: {
                    ipAddress: false,
                    language: true,
                    platform: true,
                },
                defaultTracking: {
                    sessions: true,
                    pageViews: false,
                },
                enableRequestBodyCompression: true,
            }).promise,
        track: async (event, properties) => {
            await frontendInstance.track(event, properties).promise;
        },
        flush: () => frontendInstance.flush().promise,
        reset: () => {
            frontendInstance.reset();
        },
        identify: async (uuid: string) => {
            const id = new Identify();
            await frontendInstance.identify(id, { user_id: uuid }).promise;
        },
        setUserProperties: async (props) => {
            const id = new Identify();
            Object.entries(props).forEach(
                ([key, value]: [string, Types.ValidPropertyType]) => {
                    id.set(key, value);
                }
            );
            await frontendInstance.identify(id).promise;
        },
        register: async (props) => {
            const id = new Identify();
            Object.entries(props).forEach(
                ([key, value]: [string, Types.ValidPropertyType]) => {
                    id.set(key, value);
                }
            );
            await frontendInstance.identify(id).promise;
        },
        isLoaded: () => true,
        raw: frontendInstance,
    };
}
